import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Pagination } from '../../components/ui/Pagination'
import { SegmentedButton } from '../../components/ui/SegmentedButton'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { ToastContainer } from '../../components/ui/Toast'
import { BrandValidationDialog } from '../../components/brands/BrandValidationDialog'
import { useToast } from '../../hooks/useToast'
import { useBrands } from '../../hooks/useBrands'
import { useDebounce } from '../../hooks/useDebounce'
import { Brand } from '../../types/brand'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

export const BrandsPage: React.FC = () => {
  const location = useLocation()
  
  // Get initial filters from navigation state
  const initialFilters = location.state?.toValidate === true 
    ? { toValidate: true } 
    : undefined
  
  const { 
    brands, 
    isLoading, 
    error, 
    filters,
    setFilters, 
    resetFilters,
    createBrand,
    updateBrand,
    deleteBrand,
    validateBrand,
    mergeBrand,
    refetch
  } = useBrands(initialFilters)

  const { toasts, showToast, removeToast } = useToast()
  const [searchQuery, setSearchQuery] = useState(filters.search || '')
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showValidationDialog, setShowValidationDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [brandName, setBrandName] = useState('')

  // Clear navigation state to avoid re-applying on refresh
  useEffect(() => {
    if (location.state?.toValidate === true) {
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Listen for brand update events
  useEffect(() => {
    const handleBrandUpdate = () => {
      refetch()
    }

    window.addEventListener('brandUpdated', handleBrandUpdate)
    return () => {
      window.removeEventListener('brandUpdated', handleBrandUpdate)
    }
  }, [refetch])

  React.useEffect(() => {
    // Only search if we have 0 characters (reset) or at least 3 characters
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
      if (debouncedSearch !== filters.search) {
        setFilters({ search: debouncedSearch })
      }
    }
  }, [debouncedSearch, filters.search, setFilters])

  const handleStatusFilter = (toValidate: boolean | null) => {
    setFilters({ toValidate })
  }

  const handleAdd = () => {
    setBrandName('')
    setShowAddDialog(true)
  }

  const handleEdit = (brand: Brand) => {
    setSelectedBrand(brand)
    setBrandName(brand.name)
    setShowEditDialog(true)
  }

  const handleValidate = (brand: Brand) => {
    setSelectedBrand(brand)
    setShowValidationDialog(true)
  }

  const handleDelete = (brand: Brand) => {
    setSelectedBrand(brand)
    setShowDeleteDialog(true)
  }

  const submitAdd = async () => {
    if (!brandName.trim()) return

    try {
      await createBrand({
        name: brandName.trim(),
        toValidate: false
      })
      showToast('Marque créée avec succès', 'success')
      setShowAddDialog(false)
      setBrandName('')
      // Dispatch event to refresh other instances
      window.dispatchEvent(new CustomEvent('brandUpdated'))
    } catch (error) {
      showToast('Erreur lors de la création de la marque', 'error')
    }
  }

  const submitEdit = async () => {
    if (!selectedBrand || !brandName.trim()) return

    try {
      await updateBrand({
        ...selectedBrand,
        name: brandName.trim()
      })
      showToast('Marque modifiée avec succès', 'success')
      setShowEditDialog(false)
      setSelectedBrand(null)
      setBrandName('')
      // Dispatch event to refresh other instances
      window.dispatchEvent(new CustomEvent('brandUpdated'))
    } catch (error) {
      showToast('Erreur lors de la modification de la marque', 'error')
    }
  }

  const submitValidation = async (updatedName?: string) => {
    if (!selectedBrand?.uuid) return

    try {
      // Update name if provided
      if (updatedName && updatedName !== selectedBrand.name) {
        await updateBrand({
          ...selectedBrand,
          name: updatedName
        })
      }
      // Then validate
      await validateBrand(selectedBrand.uuid)
      showToast('Marque validée avec succès', 'success')
      // Dispatch event to refresh other instances
      window.dispatchEvent(new CustomEvent('brandUpdated'))
    } catch (error) {
      showToast('Erreur lors de la validation de la marque', 'error')
      throw error
    }
  }

  const submitMerge = async (mergeWithId: string) => {
    if (!selectedBrand?.uuid) return

    try {
      await mergeBrand(selectedBrand.uuid, mergeWithId)
      showToast('Marques fusionnées avec succès', 'success')
      // Dispatch event to refresh other instances
      window.dispatchEvent(new CustomEvent('brandUpdated'))
    } catch (error) {
      showToast('Erreur lors de la fusion des marques', 'error')
      throw error
    }
  }

  const confirmDelete = async () => {
    if (!selectedBrand?.uuid) return

    try {
      await deleteBrand(selectedBrand.uuid)
      showToast('Marque supprimée avec succès', 'success')
      setShowDeleteDialog(false)
      setSelectedBrand(null)
      // Dispatch event to refresh other instances
      window.dispatchEvent(new CustomEvent('brandUpdated'))
    } catch (error) {
      showToast(
        'Erreur lors de la suppression de la marque. Il est possible que des produits soient associés à cette marque.',
        'error'
      )
    }
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || filters.toValidate !== null
  }

  return (
    <PageLayout>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Marques</h1>
        <Button
          onClick={refetch}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Filters Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5" />
              Filtres
            </CardTitle>
            <Button
              onClick={handleAdd}
              variant="primary"
              className="flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Nouvelle marque
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une marque... (min. 3 caractères)"
              className="w-full pl-10"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <SegmentedButton
              options={[
                { value: null, label: 'Toutes' },
                { value: true, label: 'À valider' },
                { value: false, label: 'Validées' }
              ]}
              value={filters.toValidate}
              onChange={(value) => handleStatusFilter(value as boolean | null)}
            />

            <div className="flex items-center gap-4">
              {brands && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {brands.totalRecords} marque(s)
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('')
                  resetFilters()
                }}
                disabled={!hasActiveFilters()}
              >
                Effacer les filtres
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brands Table */}
      <Card>
        <CardContent className="p-0">
          {error && (
            <div className="p-4 text-red-600 dark:text-red-400 text-center">
              Erreur lors du chargement des marques : {error}
            </div>
          )}

          {isLoading && (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement des marques...</p>
            </div>
          )}

          {!isLoading && !error && brands && (
            <>
              {brands.data.length === 0 ? (
                <div className="p-8 text-center text-gray-600 dark:text-gray-400">
                  Aucune marque trouvée
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                          Nom
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {brands.data.map((brand) => (
                        <tr
                          key={brand.uuid}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {brand.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              brand.toValidate
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {brand.toValidate ? 'À valider' : 'Validée'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              {brand.toValidate ? (
                                <button
                                  onClick={() => handleValidate(brand)}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                  title="Valider la marque"
                                >
                                  <CheckCircleIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleEdit(brand)}
                                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(brand)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {brands && brands.data.length > 0 && (
                <div className="p-4 border-t dark:border-gray-700">
                  <Pagination
                    currentPage={brands.currentPage}
                    totalPages={brands.lastPage}
                    pageSize={filters.limit}
                    totalRecords={brands.totalRecords}
                    onPageChange={(page) => setFilters({ page })}
                    onPageSizeChange={(limit) => setFilters({ limit })}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Brand Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              Nouvelle marque
            </h3>
            <div className="mb-4">
              <label htmlFor="add-brand-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom de la marque <span className="text-red-500">*</span>
              </label>
              <Input
                id="add-brand-name"
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Entrez le nom de la marque"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowAddDialog(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitAdd}
                disabled={!brandName.trim()}
              >
                Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Brand Dialog */}
      {showEditDialog && selectedBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              Modifier la marque
            </h3>
            <div className="mb-4">
              <label htmlFor="edit-brand-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom de la marque <span className="text-red-500">*</span>
              </label>
              <Input
                id="edit-brand-name"
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Entrez le nom de la marque"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false)
                  setSelectedBrand(null)
                }}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitEdit}
                disabled={!brandName.trim()}
              >
                Modifier
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Validation Dialog */}
      {selectedBrand && (
        <BrandValidationDialog
          isOpen={showValidationDialog}
          onClose={() => {
            setShowValidationDialog(false)
            setSelectedBrand(null)
          }}
          brand={selectedBrand}
          onValidate={submitValidation}
          onMerge={submitMerge}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
          setSelectedBrand(null)
        }}
        onConfirm={confirmDelete}
        title="Supprimer la marque"
        message={`Êtes-vous sûr de vouloir supprimer la marque "${selectedBrand?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}